import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Smartphone, AlertTriangle, CheckCircle2, X } from "lucide-react";

/**
 * Modal de configuration de la 2FA admin (TOTP / Google Authenticator).
 * Workflow :
 *   1. /auth/2fa/setup → reçoit secret + QR
 *   2. user scanne, saisit code à 6 chiffres
 *   3. /auth/2fa/enable → valide & active
 */
export default function TwoFactorSettings({ open, onClose }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(!!user?.totp_enabled);
  const [step, setStep] = useState("idle"); // idle | setup | verify | done | disable
  const [data, setData] = useState(null); // { secret, qr_data_url }
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Récupère le profil à jour
    if (open) {
      api.get("/auth/me").then((r) => setEnabled(!!r.data?.totp_enabled)).catch(() => {});
      setStep("idle"); setCode(""); setData(null);
    }
  }, [open]);

  const start = async () => {
    setLoading(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setData(r.data);
      setStep("verify");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const enable = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/2fa/enable", { code: code.trim() });
      setEnabled(true); setStep("done"); toast.success("2FA activée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code invalide");
    } finally { setLoading(false); }
  };

  const disable = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code: code.trim() });
      setEnabled(false); setStep("idle"); toast.success("2FA désactivée"); setCode("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code invalide");
    } finally { setLoading(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4 sm:p-6" data-testid="2fa-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" />
      <div className="relative bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-sm shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 w-9 h-9 grid place-items-center text-ink/70 hover:text-ink hover:bg-bone rounded-sm">
          <X className="w-5 h-5" />
        </button>
        <div className="p-7">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-ink text-gold rounded-sm grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <div className="eyebrow text-gold-dark">Sécurité du compte</div>
              <h2 className="font-serif text-2xl text-ink leading-tight">Double authentification</h2>
            </div>
          </div>

          {enabled && step !== "disable" ? (
            <>
              <div className="mt-5 bg-emerald-50 border border-emerald-200 p-4 rounded-sm flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-emerald-900 text-sm">2FA active</div>
                  <div className="text-xs text-emerald-800/85 mt-0.5">Un code à 6 chiffres sera demandé à chaque connexion.</div>
                </div>
              </div>
              <button onClick={() => setStep("disable")}
                      className="mt-4 text-sm text-crimson hover:underline" data-testid="2fa-disable-btn">
                Désactiver la 2FA
              </button>
            </>
          ) : step === "disable" ? (
            <form onSubmit={disable} className="mt-5 space-y-3">
              <p className="text-sm text-foreground/70">Saisissez votre code 2FA actuel pour confirmer la désactivation.</p>
              <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6} pattern="[0-9]{6}"
                     placeholder="000000" data-testid="2fa-disable-code"
                     className="w-full border border-border rounded-sm p-4 text-center text-2xl tracking-[0.5em] font-mono focus:border-ink outline-none" />
              <div className="flex gap-2">
                <button type="button" onClick={() => { setStep("idle"); setCode(""); }} className="btn-outline flex-1 justify-center text-sm">Annuler</button>
                <button disabled={loading} className="flex-1 px-4 py-2 bg-crimson text-white text-sm font-semibold rounded-sm hover:bg-crimson/90" data-testid="2fa-disable-confirm">
                  {loading ? "…" : "Désactiver"}
                </button>
              </div>
            </form>
          ) : step === "idle" ? (
            <>
              <p className="mt-5 text-sm text-foreground/75 leading-relaxed">
                Protégez votre compte administrateur en ajoutant une application d'authentification
                (Google Authenticator, Authy, 1Password, Microsoft Authenticator…).
              </p>
              <div className="mt-5 grid grid-cols-3 gap-3 text-center">
                <Step n="1" t="Installer une app" icon={Smartphone} />
                <Step n="2" t="Scanner le QR" icon={ShieldCheck} />
                <Step n="3" t="Saisir le code" icon={KeyRound} />
              </div>
              <button onClick={start} disabled={loading} className="btn-primary w-full justify-center mt-6" data-testid="2fa-start-btn">
                {loading ? "…" : "Commencer la configuration"}
              </button>
            </>
          ) : step === "verify" && data ? (
            <form onSubmit={enable} className="mt-5 space-y-4">
              <div className="bg-bone p-5 rounded-sm text-center">
                <img src={data.qr_data_url} alt="QR 2FA" className="w-44 h-44 mx-auto bg-white p-2 rounded-sm" />
                <p className="text-xs text-foreground/70 mt-3 leading-relaxed">
                  Scannez ce QR avec votre application d'authentification.
                </p>
                <details className="mt-2 text-[0.7rem]">
                  <summary className="cursor-pointer text-foreground/55">Ou saisissez la clé manuellement</summary>
                  <code className="block mt-1 font-mono text-xs bg-white p-2 rounded break-all">{data.secret}</code>
                </details>
              </div>
              <label className="block text-[0.7rem] uppercase tracking-[0.16em] font-semibold text-foreground/60">
                Code à 6 chiffres affiché par votre app
              </label>
              <input required autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                     inputMode="numeric" maxLength={6} pattern="[0-9]{6}" placeholder="000000"
                     data-testid="2fa-verify-code"
                     className="w-full border border-border rounded-sm p-4 text-center text-2xl tracking-[0.5em] font-mono focus:border-ink outline-none" />
              <button disabled={loading} className="btn-primary w-full justify-center" data-testid="2fa-verify-submit">
                {loading ? "…" : "Activer la 2FA"}
              </button>
            </form>
          ) : step === "done" ? (
            <div className="mt-6 text-center space-y-3">
              <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
              <p className="font-serif text-xl text-ink">2FA activée avec succès</p>
              <p className="text-sm text-foreground/65">À votre prochaine connexion, un code vous sera demandé.</p>
              <button onClick={onClose} className="btn-primary justify-center" data-testid="2fa-done-close">Fermer</button>
            </div>
          ) : null}

          {!enabled && step === "idle" && (
            <div className="mt-5 bg-amber-50 border border-amber-200 p-3 rounded-sm flex gap-2 text-xs">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span className="text-amber-900">Recommandé pour tout compte administrateur.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ n, t, icon: Icon }) {
  return (
    <div className="bg-bone p-4 rounded-sm">
      <Icon className="w-5 h-5 mx-auto text-gold-dark" />
      <div className="text-[0.55rem] uppercase tracking-[0.18em] text-foreground/55 mt-2">Étape {n}</div>
      <div className="text-xs text-ink mt-1 font-semibold">{t}</div>
    </div>
  );
}
